import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import axios from 'redaxios'
import { config } from '../../../constants'
import { IFlowCore, IFlowSubscriber } from '../../../modules/epflow'
import { Locale } from '../../../modules/locale'
import { ConsoleLog } from '../../../modules/logging'
import MainModule from '../../../modules/main'
import { getCardPlaceholder } from '../../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'lvc-create-contribution-event-preview', {
  title: "Résumé de l'événement",
  description:
    "Veuillez prendre connaissance des informations fournies sur l'événement. Vous pouvez ensuite associer votre lien de contribution à l'événement en entier ou à des représentations spécifiques.",
  seeContributors: 'Voir les contributeurs',
  hideContributors: 'Voir moins',
  addToEvent: "Associer un lien de contribution à l'événement",
  removeFromEvent: "Retirer le lien de contribution de l'événement",
  representationsLabel: 'Représentations',
  contributorsLabel: 'Contributeurs',
  noRepresentations: 'Aucune représentation disponible pour cet événement.',
})

Locale.getInstance().registerKeySet('en', 'lvc-create-contribution-event-preview', {
  title: 'Event Summary',
  description:
    'Read through the information associated with your event. Then link the Contributor to the event as a whole or to specific shows.',
  seeContributors: 'See Contributors',
  hideContributors: 'See less',
  addToEvent: 'Add a contribution link to the event',
  removeFromEvent: 'Remove contribution link from the event',
  representationsLabel: 'Representations',
  contributorsLabel: 'Contributors',
  noRepresentations: 'No representations available for this event.',
})

export interface LvcCreateContributionEventPreview {
  flowGroup: string
  flowStep: string
}

export class LvcCreateContributionEventPreview
  extends MinzeElement
  implements MinzeElement, IFlowSubscriber
{
  private flowCore: IFlowCore | undefined

  attrs: Attrs = [
    ['flow-step', ''],
    ['flow-group', ''],
  ]

  eventData: any = null
  eventOffers: any[] = []
  eventImage: string = ''
  eventName: string = ''
  eventDescription: string = ''
  eventDisciplines: string = ''
  showContributors: boolean = false
  addToParentEvent: boolean = false
  selectedOfferIds: string[] = []
  contributionType: string = ''
  isOrganizer: boolean = false

  reactive: Reactive = [
    ['eventData', null],
    ['eventOffers', []],
    ['eventImage', ''],
    ['eventName', ''],
    ['eventDescription', ''],
    ['eventDisciplines', ''],
    ['showContributors', false],
    ['addToParentEvent', false],
    ['selectedOfferIds', []],
    ['contributionType', ''],
    ['isOrganizer', false],
  ]

  async onReady(): Promise<void> {
    const mainModule = MainModule.getInstance()
    mainModule?.getFlowManager()?.subscribe(this)
    this.flowCore = MainModule.getInstance().getFlowManager().getCore(this.flowGroup)
    this.rerender()
  }

  onDestroy() {
    MainModule?.getInstance()?.getFlowManager()?.unsubscribe(this)
  }

  public async deserialize(stepData: any, data: any): Promise<void> {
    ConsoleLog(true, 'Event preview deserialize - stepData:', stepData)
    ConsoleLog(true, 'Event preview deserialize - data:', data)

    // Get contribution type from previous step to check if Organizer
    const contributionTypeData = data?.createContributionSelectType
    this.contributionType =
      contributionTypeData?.contributionType || contributionTypeData?.type || ''
    this.isOrganizer =
      this.contributionType.toLowerCase() === 'organizer' ||
      this.contributionType.toLowerCase() === 'organisateur'

    // Get the selected event data from the step data
    if (stepData?.data) {
      this.eventData = stepData.data
      this.eventOffers = stepData.offers || []

      // Get event image
      const thumbnailImageObject = this.eventData?.medias?.[0]?.media
      let thumbnailImage = ''
      if (thumbnailImageObject) {
        try {
          const mediaObject = JSON.parse(decodeURIComponent(thumbnailImageObject))
          thumbnailImage = mediaObject?.[0]?.url
        } catch (e) {
          ConsoleLog(true, 'Error parsing media:', e)
        }
      }

      if (thumbnailImage) {
        this.eventImage = thumbnailImage
      } else {
        this.eventImage = getCardPlaceholder(this.eventData?.type)
      }

      // Get event name and description
      this.eventName =
        this.eventData?.[Locale.language === 'fr' ? 'nameFr' : 'nameEn'] ||
        this.eventData?.name?.[Locale.language] ||
        ''
      this.eventDescription =
        this.eventData?.[Locale.language === 'fr' ? 'descriptionFr' : 'descriptionEn'] ||
        this.eventData?.description?.[Locale.language] ||
        ''

      // Get event disciplines
      const disciplines = this.eventData?.disciplines || []
      this.eventDisciplines = JSON.stringify(disciplines)
    }

    // Restore any previously selected state
    if (stepData?.addToParentEvent) {
      this.addToParentEvent = stepData.addToParentEvent
    }
    if (stepData?.selectedOfferIds) {
      this.selectedOfferIds = stepData.selectedOfferIds
    }

    this.rerender()
  }

  public async serialize(): Promise<unknown> {
    return {
      data: this.eventData,
      offers: this.eventOffers,
      addToParentEvent: this.addToParentEvent,
      selectedOfferIds: this.selectedOfferIds,
      selectedOffers: this.eventOffers.filter((offer: any) =>
        this.selectedOfferIds.includes(String(offer.id))
      ),
    }
  }

  public async validate(): Promise<boolean> {
    // Check if we can add to parent event
    const canAddToParentEvent = this.shouldShowAddToEventButton()

    if (canAddToParentEvent) {
      // Must select at least one thing - parent event or at least one representation
      if (!this.addToParentEvent && this.selectedOfferIds.length === 0) {
        MainModule.getInstance().showToastAlert(
          "Veuillez sélectionner au moins une représentation ou ajouter le lien à l'événement.",
          3000,
          true
        )
        return false
      }
    } else {
      // Organisateur + regular event: can only add to representations
      if (this.selectedOfferIds.length === 0) {
        MainModule.getInstance().showToastAlert(
          'Veuillez sélectionner au moins une représentation.',
          3000,
          true
        )
        return false
      }
    }
    return true
  }

  onToggleContributors(): void {
    this.showContributors = !this.showContributors
    this.rerender()
  }

  onToggleParentEvent(): void {
    this.addToParentEvent = !this.addToParentEvent
    this.rerender()
  }

  onToggleOffer(offerId: string): void {
    if (this.selectedOfferIds.includes(offerId)) {
      this.selectedOfferIds = this.selectedOfferIds.filter((id: string) => id !== offerId)
    } else {
      this.selectedOfferIds = [...this.selectedOfferIds, offerId]
    }
    this.rerender()
  }

  getFormattedDate(offer: any): string {
    const startDate = offer.startDate
    if (!startDate) return ''

    try {
      const dateInMs =
        typeof startDate === 'number'
          ? startDate < 10000000000
            ? startDate * 1000
            : startDate
          : new Date(startDate).getTime()

      const formattedDate = new Date(dateInMs).toLocaleDateString(Locale.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'EST',
      })

      const startTime =
        typeof offer.startTime === 'string'
          ? offer.startTime
          : offer.startTime?.[Locale.language] || ''

      return `${formattedDate}${startTime ? `, ${startTime}` : ''}`
    } catch (e) {
      return ''
    }
  }

  getOfferLocation(offer: any): string {
    return offer.location?.[Locale.language] || ''
  }

  /**
   * Determines whether to show the "Add contribution link to event" button.
   *
   * Business rule:
   * - For "Organisateur" contribution type: Hide the button for regular events (Événement),
   *   but show it for exhibitions and series.
   * - For all other contribution types: Always show the button.
   */
  shouldShowAddToEventButton(): boolean {
    if (!this.isOrganizer) {
      // Non-organizer contribution types can always add to any event type
      return true
    }

    // For Organizer contribution type, check the event type
    const eventTable = this.eventData?.table || ''

    // Hide button only for regular events (table === 'events')
    // Show button for exhibitions (exhibitionEvents) and series (eventSeries)
    if (eventTable === 'events') {
      return false
    }

    // Show button for exhibitions and series
    return true
  }

  renderEventCard(): string {
    const contributors =
      this.eventData?.artists?.map((artist: any) => ({
        ...artist,
        contributorName: artist[Locale.language === 'fr' ? 'nameFr' : 'nameEn'],
      })) || []

    return `
      <lvc-box background="white" borderRadius="8px" border="1px solid #1E007D4D" overflow="hidden">
        ${
          this.eventImage
            ? `
          <img 
            src="${this.eventImage}" 
            alt="${this.eventName}"
            style="width: 100%; aspect-ratio: 16/9; object-fit: cover;"
          />
        `
            : ''
        }
        <lvc-box padding="24px">
          <lvc-box mb="16px">
            <lvc-text font-preset="largeExtraBold" text-transform="uppercase">
              ${this.eventName}
            </lvc-text>
          </lvc-box>
          ${
            this.eventDescription
              ? `
            <lvc-box mb="16px">
              <lvc-text font-preset="small" line-height="22px">
                ${this.eventDescription}
              </lvc-text>
            </lvc-box>
          `
              : ''
          }
          ${
            contributors.length
              ? `
            <hr class="divider"/>
            ${
              this.showContributors
                ? `
              <lvc-box mb="16px">
                <lvc-text font-preset="medium">
                  ${Locale.getKey('lvc-create-contribution-event-preview.contributorsLabel')}
                </lvc-text>
                <lvc-box mt="16px">
                  ${contributors
                    .map(
                      (contributor: any) => `
                    <lvc-box mb="16px">
                      <lvc-box mb="8px">
                        <lvc-text font-preset="smallExtraBold">
                          ${contributor.contributorName}
                        </lvc-text>
                      </lvc-box>
                    </lvc-box>
                  `
                    )
                    .join('')}
                </lvc-box>
              </lvc-box>
            `
                : ''
            }
            <lvc-button 
              theme="transparent" 
              height="40px" 
              border="1px solid #1E007D" 
              name="toggleContributors"
              width="100%"
            >
              ${Locale.getKey(
                this.showContributors
                  ? 'lvc-create-contribution-event-preview.hideContributors'
                  : 'lvc-create-contribution-event-preview.seeContributors'
              )}
            </lvc-button>
          `
              : ''
          }
          ${
            this.shouldShowAddToEventButton()
              ? `
            <lvc-box mt="8px">
              <lvc-button 
                theme="${this.addToParentEvent ? 'primary' : 'transparent'}" 
                height="40px" 
                border="1px solid #1E007D"
                name="toggleParentEvent"
                class="${this.addToParentEvent ? 'selected' : ''}"
                width="100%"
              >
                ${Locale.getKey(
                  this.addToParentEvent
                    ? 'lvc-create-contribution-event-preview.removeFromEvent'
                    : 'lvc-create-contribution-event-preview.addToEvent'
                )}
              </lvc-button>
            </lvc-box>
          `
              : ''
          }
        </lvc-box>
      </lvc-box>
    `
  }

  html = () => `
    <div class="container">
      <lvc-box mt="24px" textAlign="left">
        <lvc-text font-preset="titleMobileSmall">
          ${Locale.getKey('lvc-create-contribution-event-preview.title')}
        </lvc-text>
      </lvc-box>

      <lvc-box mt="24px" textAlign="left">
        <lvc-text font-preset="medium">
          ${Locale.getKey('lvc-create-contribution-event-preview.description')}
        </lvc-text>
      </lvc-box>

      <lvc-box mt="24px">
        ${this.renderEventCard()}
      </lvc-box>

      ${this.renderRepresentations()}
    </div>
  `

  renderRepresentations(): string {
    if (!this.eventOffers?.length) {
      return ''
    }

    return `
      <lvc-box mt="32px">
        <lvc-box mb="16px">
          <lvc-text font-preset="medium" font-weight="600">
            ${Locale.getKey('lvc-create-contribution-event-preview.representationsLabel')}
          </lvc-text>
        </lvc-box>
        <lvc-box display="flex" flexDirection="column" rowGap="16px">
          ${this.eventOffers
            .map((offer: any, index: number) => {
              const isSelected =
                this.selectedOfferIds.includes(offer.id) ||
                this.selectedOfferIds.includes(String(offer.id))

              return `
              <div class="representation-block">
                <lvc-representation-block
                  representation-data="${encodeURIComponent(JSON.stringify(offer))}"
                  representation-index="${index}"
                  flow-group="${this.flowGroup}"
                  is-existing="true"
                  show-type="true"
                  hide-price="true"
                  compact="true"
                ></lvc-representation-block>
                <div 
                  class="select-offer ${isSelected ? 'selected' : ''}"
                  data-offer-id="${offer.id}"
                  name="select-offer-${offer.id}"
                >
                  ${
                    isSelected
                      ? '<lvc-close-icon width="20" height="20"></lvc-close-icon>'
                      : '<lvc-add-icon class="add-offer-icon" data-color="#1E007D"></lvc-add-icon>'
                  }
                </div>
              </div>
            `
            })
            .join('')}
        </lvc-box>
      </lvc-box>
    `
  }

  css = () => `
    :host {
      display: block;
    }

    .representation-block {
      position: relative;
    }

    .select-offer {
      position: absolute;
      top: 16px;
      right: 16px;
      padding: 6px;
      border-radius: 999px;
      border: 2px solid #1E007D;
      background: #F5F5F5;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 32px;
      height: 32px;
      box-sizing: border-box;
      transition: all 0.2s ease;
      user-select: none;
    }

    .select-offer.selected {
      background-color: #BFF0FF;
      border-color: #1E007D;
    }

    .select-offer:hover {
      background-color: #e0e0ff;
    }

    lvc-add-icon {
      display: flex;
      justify-content: center;
    }

    .add-offer-icon {
      margin-right: 0;
    }

    lvc-button.selected {
      background-color: #BFF0FF;
    }
    lvc-button.selected {
      background-color: #BFF0FF;
    }

    .divider {
      background: #1E007D;
      height: 1px;
      border: none;
      margin: 16px 0;
      opacity: 0.3;
    }
  `

  onOfferClick(e: Event): void {
    const target = (e.target as Element)?.closest('.select-offer')
    if (target) {
      const offerId = target.getAttribute('data-offer-id')
      if (offerId) {
        this.onToggleOffer(offerId)
        e.stopPropagation() // Prevent bubbling if needed
      }
    }
  }

  eventListeners: EventListeners = [
    ['[name="toggleParentEvent"]', 'click', this.onToggleParentEvent.bind(this)],
    ['[name="toggleContributors"]', 'click', this.onToggleContributors.bind(this)],
    ['.select-offer', 'click', this.onOfferClick.bind(this)],
  ]
}
